import React, { useEffect, useMemo, useState } from "react";
import { Image, Spin } from "antd";
import { Button } from "Components";
import StepFooter from "../../components/StepFooter";
import { DIFFICULTY_COLOR } from "../../PhishingManager.constants";
import { fetchCampaignReviewData } from "PhishingManager/service/index.Service";
import { capitalize, formatDateTime, joinItems } from "PhishingManager/utils";
import EmptyState from "PhishingManager/components/EmptyState";
import { CampaignReviewData } from "./Step5.types";

interface StepProps {
  tenantId: number | string | undefined;
  campaignId: string | number | null;
  showSummary: boolean;
  defaultCampaignData: CampaignReviewData | null;
  onBack: () => void;
  onFinish: () => void;
  showPreview: (id: string | number, languageId: number | null) => void;
  setToast: (toast: { message: string; type: 'warning' | 'success' }) => void;
}

const Step5ReviewStart: React.FC<StepProps> = ({
  tenantId,
  campaignId,
  onBack,
  onFinish,
  showPreview,
  defaultCampaignData,
  showSummary,
  setToast
}) => {
  const [campaignData, setCampaignData] = useState<CampaignReviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const tag = capitalize(campaignData?.packaged_template?.difficulty || '') || 'Beginner';
  const duration = campaignData?.delivery_and_schedule?.campaign_length || 0;
  const { bg, color } = DIFFICULTY_COLOR[tag];
  const templateId = campaignData?.packaged_template?.id || 0;
  const orgType = campaignData?.full_campaign_audience?.selected_type || "";
  const audienceLabel = useMemo(() => {
    let label = "Properties";
    if (orgType == "groups") label = "Groups";
    else if (orgType == "roles") label = "Roles";
    else if (orgType == "organization") label = "Users";
    else if (orgType == "individuals") label = "Individuals";

    return `${label} selected`;
  }, [orgType])

  const handleSubmit = () => {
    if (!campaignData || isLoading) return;
    onFinish();
  }

  useEffect(() => {
    if (defaultCampaignData) setCampaignData({ ...defaultCampaignData });
    else {
      (async function () {
        if (!tenantId || !campaignId) return;
        setIsLoading(true);
        const { data, error } = await fetchCampaignReviewData(tenantId, campaignId);
        if (error) {
          setToast({ message: error, type: "warning" });
          setCampaignData(null);
        } else setCampaignData(data);
        setIsLoading(false);
      })();
    }
  }, [])

  return (
    <Spin spinning={isLoading}>
      <div className="review-container step-card">
        {campaignData ? <>
          {!showSummary &&
            <div className="card-header">
              <div className="card-title">Review & Start Test</div>
              <div className="card-subtitle">
                Review the configuration below and start the test campaign
              </div>
            </div>}

          <StepDetailSection title="Scenario">
            <div className="template-preview">
              <Image alt={campaignData?.packaged_template?.name || ''} src={campaignData?.packaged_template?.image_path} fallback='./placeholderImage.png' />
              <div className="campaign-card">
                <span className="campaign-card-title">{campaignData?.packaged_template?.name || ''}</span>
                <span className="campaign-card-status" style={{ color, background: bg }}>{tag}</span>
                <span className="campaign-card-tag">{joinItems(campaignData?.packaged_template?.categories || '')}</span>
              </div>
              {templateId && <Button shape="round" type="primary" ghost onClick={() => showPreview(templateId, showSummary ? campaignData?.packaged_template?.selected_language_id : null)}>Preview</Button>}
            </div>
          </StepDetailSection>

          <StepDetailSection title="Test Configuration">
            <CardStep title="Test recipients (required)" subTitle={joinItems(campaignData?.test_recipients || '')} />
          </StepDetailSection>

          <StepDetailSection title="Full Campaign Audience">
            <>
              <CardStep title="Selected method" subTitle={orgType} doCapitalize={true} />
              <CardStep title={audienceLabel} subTitle={joinItems(campaignData?.full_campaign_audience?.values || '')} doCapitalize={orgType !== "individuals"} />
            </>
          </StepDetailSection>

          <StepDetailSection title="Delivery & Schedule" cardClass="card-grid">
            <>
              <CardStep title="Start date" subTitle={formatDateTime(campaignData?.delivery_and_schedule?.start_date || '', "M/D/YYYY")} />
              <CardStep title="Start time" subTitle={formatDateTime(campaignData?.delivery_and_schedule?.start_time || '', "h:mm A")} />
              <CardStep title="Duration" subTitle={`${duration} day${Number(duration) > 1 ? 's' : ''}`} />
              <CardStep title="Timezone" subTitle={campaignData?.delivery_and_schedule?.timezone || ''} />
            </>
          </StepDetailSection>
        </> : isLoading ? <div className="cs-empty-state">Loading...</div> : 
        <div className="empty-data">
          <EmptyState
            title="No data to display yet"
            description={["Your campaign reports will appear here once you launch your first simulation."]}
            actionLabel={null} />
          </div>}
      </div>
      {!showSummary &&
        <StepFooter
          nextBtnText="Start test"
          onBack={onBack}
          onNext={handleSubmit}
          nextBtnDisabled={!campaignData}
        />}
    </Spin>
  );
};

export default Step5ReviewStart;

const StepDetailSection = ({ title, children, cardClass = '' }: { title: string, cardClass?: string, children: React.ReactElement }) => {
  return <div className="step-detail-section">
    <span className="step-title">{title}</span>
    <div className={`card ${cardClass}`}>{children}</div>
  </div>
}

const CardStep = ({ title, subTitle, doCapitalize = false }: { title: string, subTitle: string, doCapitalize?: boolean }) => {
  return <div className="card-step">
    <div className="card-step-title">{title}</div>
    <div className="card-step-subtitle" style={doCapitalize ? { textTransform: "capitalize" } : {}}>{subTitle}</div>
  </div>
}